import {
	ALIAS_DESTINATION_TYPES,
	ENGAGE_EVENT_TYPES,
	ENGAGE_INBOX_NETWORKS,
	ENGAGE_LOGIN_METHODS,
	engageLooksLikeEmail,
	engageLoginPhoneHint,
	normalizeEngageLoginPhone,
	ORGANISATION_ROLES,
	SOCIAL_NETWORKS,
	WORKSPACE_APPS,
	SOURCE_SECURITY,
	SUPPORT_CATEGORIES,
	SUPPORT_PRIORITIES,
} from "@bafe/types";
import { z } from "zod";

import {
	isApexHostname,
	normalizeDomain,
	normalizeEmail,
	normalizeLocalPart,
	validateDomain,
	validateLocalPart,
} from "./domain-name.js";
import { normalizeHubBookList } from "./hub-book-list.js";
import { checkAccountPassword, checkMailboxPassword } from "./password.js";

/** Request schemas for `/api/v1`. Every handler parses its input through one. */

export const uuid = z.string().uuid("Invalid identifier.");

export const emailSchema = z
	.string()
	.trim()
	.min(3)
	.max(320)
	.email("Enter a valid email address.")
	.transform(normalizeEmail);

export const domainSchema = z
	.string()
	.trim()
	.min(1, "Enter a domain name.")
	.transform(normalizeDomain)
	.superRefine((value, ctx) => {
		const result = validateDomain(value);
		if (!result.ok) {
			ctx.addIssue({ code: z.ZodIssueCode.custom, message: result.message ?? "Invalid domain." });
		}
	});

export const accountPasswordSchema = z.string().superRefine((value, ctx) => {
	const result = checkAccountPassword(value);
	if (!result.ok) ctx.addIssue({ code: z.ZodIssueCode.custom, message: result.message! });
});

export const mailboxPasswordSchema = z.string().superRefine((value, ctx) => {
	const result = checkMailboxPassword(value);
	if (!result.ok) ctx.addIssue({ code: z.ZodIssueCode.custom, message: result.message! });
});

// --- Authentication -------------------------------------------------------

export const registerSchema = z.object({
	fullName: z.string().trim().min(1, "Enter your name.").max(120),
	email: emailSchema,
	password: accountPasswordSchema,
	organisationName: z.string().trim().min(1, "Enter your organisation name.").max(160),
	country: z.string().trim().length(2).toUpperCase().default("MY"),
	timezone: z.string().trim().max(64).default("Asia/Kuala_Lumpur"),
});

/** Email or a unique username (the local part of the stored address). */
export const loginIdentifierSchema = z
	.string()
	.trim()
	.min(1, "Enter your email or username.")
	.max(320)
	.transform((value) => value.toLowerCase());

export const loginSchema = z.object({
	email: loginIdentifierSchema,
	password: z.string().min(1, "Enter your password."),
	totpCode: z.string().trim().regex(/^\d{6}$/, "Enter the 6-digit code.").optional(),
	recoveryCode: z.string().trim().min(8).max(64).optional(),
});

export const requestPasswordResetSchema = z.object({ email: emailSchema });

export const confirmPasswordResetSchema = z.object({
	token: z.string().min(16),
	password: accountPasswordSchema,
});

export const enableTotpSchema = z.object({
	code: z.string().trim().regex(/^\d{6}$/, "Enter the 6-digit code."),
});

export const reauthenticateSchema = z.object({ password: z.string().min(1) });

// --- Organisation ---------------------------------------------------------

export const updateOrganisationSchema = z.object({
	name: z.string().trim().min(1).max(160).optional(),
	legalName: z.string().trim().max(200).nullable().optional(),
	registrationNumber: z.string().trim().max(60).nullable().optional(),
	billingEmail: emailSchema.optional(),
	country: z.string().trim().length(2).toUpperCase().optional(),
	timezone: z.string().trim().max(64).optional(),
});

export const inviteMemberSchema = z.object({
	email: emailSchema,
	role: z.enum(ORGANISATION_ROLES).refine((role) => role !== "owner", {
		message: "Transfer ownership instead of inviting a second owner.",
	}),
});

export const provisionInvitationSchema = z.object({
	password: accountPasswordSchema.optional(),
});

export const acceptInvitationSchema = z.object({
	token: z.string().trim().min(16, "This invitation link is incomplete."),
	fullName: z.string().trim().max(120).optional(),
	password: z.string().max(200).optional(),
});

export const changeMemberRoleSchema = z
	.object({
		role: z
			.enum(ORGANISATION_ROLES)
			.refine((role) => role !== "owner", {
				message: "Use ownership transfer to change the owner.",
			})
			.optional(),
		viewAllHubTasks: z.boolean().optional(),
	})
	.refine((value) => value.role !== undefined || value.viewAllHubTasks !== undefined, {
		message: "Choose what to update.",
	});

export const updateMemberHubTaskAccessSchema = z.object({
	viewAllHubTasks: z.boolean(),
});

export const transferOwnershipSchema = z.object({
	userId: uuid,
	confirmOrganisationName: z.string().trim().min(1),
});

// --- Domains --------------------------------------------------------------

export const createDomainSchema = z
	.object({
		domain: domainSchema,
		email: z.boolean().optional(),
		sites: z.boolean().optional(),
		platform: z.enum(["hub", "webmail"]).optional(),
		includeWww: z.boolean().optional(),
	})
	.transform((input) => {
		const specified =
			input.email !== undefined || input.sites !== undefined || input.platform !== undefined;
		const platform = input.platform;
		const email = platform ? false : specified ? (input.email ?? false) : true;
		const sites = platform ? false : (input.sites ?? false);
		const apex = isApexHostname(input.domain);
		const includeWww = sites && apex ? (input.includeWww ?? true) : false;
		return { domain: input.domain, email, sites, platform, includeWww, apex };
	})
	.superRefine((value, ctx) => {
		if (value.platform) {
			if (value.apex) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["platform"],
					message: "Add hub.yourdomain.com or webmail.yourdomain.com, not the root domain.",
				});
			}
			return;
		}
		if (!value.email && !value.sites) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["email"],
				message: "Choose email, a website, or both.",
			});
		}
	});

export const updateDomainCapabilitiesSchema = z
	.object({
		email: z.boolean(),
		sites: z.boolean(),
		includeWww: z.boolean().optional(),
	})
	.refine((value) => value.email || value.sites, {
		message: "Keep at least email or a website on this domain.",
	});

export const createSiteTagSchema = z.object({
	name: z.string().trim().min(1).max(32),
});

export const setHostedSiteTagsSchema = z.object({
	tagIds: z.array(uuid).max(12),
});

export const updateHostedSiteSchema = z
	.object({
		wwwMode: z.enum(["none", "to_apex", "to_www"]).optional(),
		enabled: z.boolean().optional(),
	})
	.refine((value) => value.wwwMode !== undefined || value.enabled !== undefined, {
		message: "Choose what to change.",
	});

export const createPathSiteSchema = z.object({
	segment: z.string().trim().min(1).max(63),
});

export const siteFileMutationSchema = z.object({
	path: z.string().trim().min(1).max(500),
	content: z.string().max(2_000_000).optional(),
	name: z.string().trim().min(1).max(255).optional(),
	confirm: z.boolean().optional(),
	expectedMtimeNs: z.string().regex(/^\d+$/).optional(),
	expectedSize: z.number().int().nonnegative().optional(),
});

export const createSiteEntrySchema = z.object({
	path: z.string().trim().min(1).max(500).default("/"),
	name: z.string().trim().min(1).max(255),
	kind: z.enum(["file", "directory"]),
	content: z.string().max(1_000_000).optional(),
});

export const createSiteRedirectSchema = z.object({
	hostedSiteId: uuid,
	kind: z.enum(["host", "path"]),
	sourceHost: z.string().trim().min(1).max(253),
	sourcePath: z.string().trim().default(""),
	destination: z.string().trim().min(1).max(500),
	statusCode: z.union([z.literal(301), z.literal(302)]),
});

export const updateSiteRedirectSchema = z.object({
	destination: z.string().trim().min(1).max(500).optional(),
	sourcePath: z.string().trim().max(500).optional(),
	statusCode: z.union([z.literal(301), z.literal(302)]).optional(),
	enabled: z.boolean().optional(),
});

export const createSiteDatabaseSchema = z.object({
	name: z.string().trim().min(1).max(63),
});

export const createCustomerDatabaseRoleSchema = z.object({
	name: z.string().trim().min(1).max(63),
	password: z.string().min(1).max(256),
	passwordConfirm: z.string().min(1).max(256),
});

export const updateCustomerDatabaseRoleSchema = z.object({
	password: z.string().min(1).max(256),
	passwordConfirm: z.string().min(1).max(256),
});

export const customerDatabasePrivilegesSchema = z.object({
	role: z.string().trim().min(1).max(63),
	privileges: z.array(z.string().trim().min(1).max(40)).min(1).max(20),
});

export const deleteCustomerDatabaseSchema = z.object({
	confirm: z.string(),
});

export const removeDomainSchema = z.object({
	confirmDomain: z.string().trim().min(1),
});

export const connectCloudflareDnsSchema = z.object({
	zoneId: z.string().trim().min(1),
	/** Records the customer explicitly approved in the preview step. */
	approvedRecordIds: z.array(uuid).min(1),
});

// --- Mailboxes ------------------------------------------------------------

const localPartSchema = z
	.string()
	.trim()
	.transform(normalizeLocalPart)
	.superRefine((value, ctx) => {
		const result = validateLocalPart(value);
		if (!result.ok) ctx.addIssue({ code: z.ZodIssueCode.custom, message: result.message! });
	});

/** See-only or send-as. Owner is created with the mailbox and is not assigned here. */
export const assignMailboxAccessSchema = z.object({
	userId: uuid,
	access: z.enum(["full", "read_only"]),
	showInSidebar: z.boolean().default(false),
});

export const updateMailboxGrantSidebarSchema = z.object({
	showInSidebar: z.boolean(),
});

export const recordMessageOpensSchema = z.object({
	messageIds: z.array(z.string().trim().min(1).max(256)).min(1).max(50),
});

/** Personal sidebar layout. Names are short, and an address is listed once. */
export const saveSidebarGroupsSchema = z.object({
	groups: z
		.array(
			z.object({
				id: uuid,
				name: z
					.string()
					.trim()
					.min(1)
					.max(80)
					.transform((value) => value.replace(/\s+/g, " ").slice(0, 40)),
				mailboxIds: z.array(uuid).max(100),
				parentId: z.union([uuid, z.null()]).optional(),
				defaultExpanded: z.boolean().optional(),
			}),
		)
		.max(50),
});

/** Personal sidebar account order. Every id must be a sidebar-visible mailbox the user can open. */
export const saveSidebarAccountOrderSchema = z.object({
	mailboxIds: z.array(uuid).max(200),
});

export const unreadGroupSchema = z.object({
	id: uuid,
	name: z
		.string()
		.trim()
		.min(1, "Enter a group name.")
		.max(40)
		.transform((value) => value.replace(/\s+/g, " ")),
	mailboxIds: z.array(uuid).max(200),
});

/** Unread view grouping. Each mailbox id may appear in at most one group. */
export const saveInboxGroupingSchema = z.object({
	enabled: z.boolean(),
	groups: z.array(unreadGroupSchema).max(100),
});

const unifiedThreadSegmentSchema = z.enum(["inbox", "unread", "sent", "drafts", "spam", "trash"]);

export const unifiedThreadListSchema = z.object({
	segment: unifiedThreadSegmentSchema,
	mailboxIds: z.array(uuid).min(1).max(200),
	limitPerMailbox: z.number().int().min(1).max(80).optional(),
	cursor: z.string().max(256).nullable().optional(),
});

const mailSignatureItemSchema = z.object({
	id: uuid,
	name: z
		.string()
		.trim()
		.min(1, "Enter a signature name.")
		.max(80)
		.transform((value) => value.replace(/\s+/g, " ").slice(0, 80)),
	html: z.string().max(120_000),
	sortOrder: z.number().int().min(0).max(999),
});

const mailSignatureDefaultsSchema = z.record(
	uuid,
	z.object({
		newEmail: z.union([uuid, z.null()]),
		replyForward: z.union([uuid, z.null()]),
	}),
);

/** Personal mail signatures. Defaults may only reference mailboxes this person can send as. */
export const saveMailSignaturesSchema = z.object({
	signatures: z.array(mailSignatureItemSchema).max(20),
	defaults: mailSignatureDefaultsSchema,
});

export const mailContactBodySchema = z.object({
	email: emailSchema,
	fullName: z.string().trim().max(120).default(""),
	company: z.string().trim().max(120).nullable().optional(),
	jobTitle: z.string().trim().max(120).nullable().optional(),
	displayAs: z.string().trim().min(1, "Enter how this contact should appear.").max(160),
});

export const createMailboxSchema = z.object({
	domainId: uuid,
	localPart: localPartSchema,
	firstName: z.string().trim().max(80).default(""),
	lastName: z.string().trim().max(80).default(""),
	password: mailboxPasswordSchema,
	/** Administrative ceiling inside the shared pool. Null means pool-bound. */
	quotaBytes: z.number().int().positive().nullable().default(null),
});

export const updateMailboxSchema = z.object({
	displayName: z.string().trim().max(160).optional(),
	quotaBytes: z.number().int().positive().nullable().optional(),
});

export const resetMailboxPasswordSchema = z.object({
	password: mailboxPasswordSchema,
});

export const deleteMailboxSchema = z.object({
	confirmEmailAddress: z.string().trim().min(1),
});

// --- Aliases --------------------------------------------------------------

export const createAliasSchema = z
	.object({
		domainId: uuid,
		localPart: localPartSchema,
		destinationType: z.enum(ALIAS_DESTINATION_TYPES),
		mailboxId: uuid.nullable().default(null),
		externalAddress: emailSchema.nullable().default(null),
	})
	.superRefine((value, ctx) => {
		if (value.destinationType === "mailbox" && !value.mailboxId) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["mailboxId"],
				message: "Choose the mailbox this alias delivers to.",
			});
		}
		if (value.destinationType === "external" && !value.externalAddress) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["externalAddress"],
				message: "Enter the address to forward to.",
			});
		}
	});

// --- Migration ------------------------------------------------------------

export const createMigrationSchema = z.object({
	mailboxId: uuid,
	sourceHost: z.string().trim().min(1).max(253),
	sourcePort: z.number().int().min(1).max(65535).default(993),
	sourceSecurity: z.enum(SOURCE_SECURITY).default("ssl"),
	sourceUsername: z.string().trim().min(1).max(320),
	/**
	 * Encrypted immediately on receipt, never logged, and destroyed when the
	 * migration finishes or is cancelled.
	 */
	sourcePassword: z.string().min(1).max(512),
});

export const updateMigrationSchema = z
	.object({
		sourceHost: z.string().trim().min(1).max(253).optional(),
		sourcePort: z.number().int().min(1).max(65535).optional(),
		sourceSecurity: z.enum(["ssl", "starttls"]).optional(),
		sourceUsername: z.string().trim().min(1).max(320).optional(),
		sourcePassword: z.string().min(1).max(512).optional(),
	})
	.superRefine((value, ctx) => {
		if (
			value.sourceHost === undefined &&
			value.sourcePort === undefined &&
			value.sourceSecurity === undefined &&
			value.sourceUsername === undefined &&
			value.sourcePassword === undefined
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Change the IMAP server, port, or saved password.",
			});
		}
		const hasUsername = value.sourceUsername !== undefined;
		const hasPassword = value.sourcePassword !== undefined;
		if (hasUsername !== hasPassword) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Enter both the username and password to update the saved credential.",
			});
		}
	});

// --- Billing --------------------------------------------------------------

export const checkoutSchema = z.object({
	planCode: z.string().trim().min(1),
	billingPeriod: z.enum(["monthly", "annual"]).default("annual"),
});

export const changeStoragePlanSchema = z.object({ planCode: z.string().trim().min(1) });

// --- Support --------------------------------------------------------------

export const createSupportCaseSchema = z.object({
	category: z.enum(SUPPORT_CATEGORIES),
	priority: z.enum(SUPPORT_PRIORITIES).default("normal"),
	subject: z.string().trim().min(1).max(200),
	body: z.string().trim().min(1).max(10_000),
	/** Safe resource context only. Never message bodies or credentials. */
	context: z
		.object({
			domainId: uuid.optional(),
			mailboxId: uuid.optional(),
			migrationId: uuid.optional(),
			traceId: z.string().max(64).optional(),
		})
		.optional(),
});

export const workspaceAppListSchema = z.object({
	apps: z.array(z.enum(WORKSPACE_APPS)),
});

export const createWorkspaceSchema = z.object({
	name: z.string().trim().min(1, "Enter a workspace name.").max(160),
});

export const selectWorkspaceSchema = z.object({
	organisationId: uuid,
});

export const updateWorkspaceConfigSchema = z.object({
	name: z.string().trim().min(1, "Enter a workspace name.").max(160).optional(),
	legalName: z.string().trim().max(200).nullable().optional(),
	registrationNumber: z.string().trim().max(80).nullable().optional(),
	billingEmail: emailSchema.optional(),
	country: z.string().trim().length(2).optional(),
	timezone: z.string().trim().min(1).max(80).optional(),
	defaultMailboxQuotaBytes: z.number().int().positive().nullable().optional(),
	siteIndexPage: z.string().trim().min(1).max(80).optional(),
	siteErrorPage: z.string().trim().min(1).max(80).optional(),
	logo: z
		.string()
		.max(200_000)
		.refine((value) => value.startsWith("data:image/"), "Use an image file.")
		.nullable()
		.optional(),
});

export const memberAppGrantsSchema = z.object({
	apps: z.array(z.enum(WORKSPACE_APPS)),
});

// --- Pagination -----------------------------------------------------------

export const paginationSchema = z.object({
	limit: z.coerce.number().int().min(1).max(100).default(25),
	cursor: z.string().max(512).nullish(),
});

export type RegisterInput = z.infer<typeof registerSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type CreateDomainInput = z.infer<typeof createDomainSchema>;
export type CreateMailboxInput = z.infer<typeof createMailboxSchema>;
export type CreateAliasInput = z.infer<typeof createAliasSchema>;
export type CreateMigrationInput = z.infer<typeof createMigrationSchema>;
export type UpdateMigrationInput = z.infer<typeof updateMigrationSchema>;

const hubText = (label: string, max = 200) => z.string().trim().min(1, `Enter a ${label}.`).max(max);

export const createHubLeadSchema = z.object({
	source: z.enum(["website", "social", "walk-in", "call", "associates"]),
	site: hubText("site"),
	name: hubText("name"),
	phone: hubText("phone", 40),
	email: z.string().trim().email("Enter an email address.").max(200),
	location: hubText("location"),
	service: hubText("service"),
	page: z.string().trim().max(200).optional().default(""),
});

export const updateHubLeadSchema = createHubLeadSchema;

export const hubTaskChecklistItemSchema = z.object({
	id: z.string().trim().min(1).max(80),
	title: z.string().trim().min(1, "Enter a checklist item.").max(200),
	done: z.boolean(),
});

export const hubTaskChecklistSchema = z.array(hubTaskChecklistItemSchema).max(50);

export const HUB_TASK_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
export const HUB_TASK_ATTACHMENT_MAX_COUNT = 12;

export const hubTaskAttachmentItemSchema = z.object({
	id: z.string().trim().min(1).max(80),
	name: z.string().trim().min(1).max(255),
	type: z.string().trim().max(127).optional().default("application/octet-stream"),
	size: z.number().int().nonnegative().max(HUB_TASK_ATTACHMENT_MAX_BYTES),
	data: z.string().trim().min(1).max(7_500_000),
});

export const hubTaskAttachmentsSchema = z.array(hubTaskAttachmentItemSchema).max(HUB_TASK_ATTACHMENT_MAX_COUNT);

export const hubTaskStatusSchema = z.enum(["new", "pending", "reviewed", "completed"]);

export const createHubTaskSchema = z.object({
	title: hubText("task"),
	description: z.string().trim().max(4000).optional().default(""),
	assignee: z.string().trim().max(800).optional().default(""),
	assignees: z.array(z.string().trim().max(80)).max(12).optional(),
	assigneeUserIds: z.array(uuid).max(12).optional(),
	dueOn: z.union([z.literal(""), z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, "Enter a date.")]).optional().default(""),
	tag: z.string().trim().max(80).optional().default(""),
	tags: z.array(z.string().trim().max(80)).max(12).optional(),
	// Stored as "reviewed" so existing rows stay valid. The UI label is Need Review.
	status: hubTaskStatusSchema.optional().default("new"),
	checklist: hubTaskChecklistSchema.optional().default([]),
	checklistTitle: z.string().trim().max(200).optional().default(""),
	attachments: hubTaskAttachmentsSchema.optional().default([]),
	boardPosition: z.number().finite().optional(),
});

export const deleteHubTagSchema = z.object({
	name: z.string().trim().min(1, "Enter a tag.").max(80),
});

export const hubTagColorIds = [
	"zinc",
	"slate",
	"stone",
	"sky",
	"cyan",
	"indigo",
	"navy",
	"violet",
	"fuchsia",
	"rose",
	"coral",
	"amber",
	"orange",
	"olive",
	"lime",
	"mint",
	"teal",
] as const;

export const hubTagColorIdSchema = z.enum(hubTagColorIds);

export const hubHexColorSchema = z
	.string()
	.regex(/^#[0-9a-fA-F]{6}$/, "Enter a colour.")
	.transform((value) => value.toLowerCase());

export const hubTagColorValueSchema = z.union([hubTagColorIdSchema, hubHexColorSchema]);

export const updateHubTagSchema = z.object({
	name: z.string().trim().min(1, "Enter a tag.").max(80),
	color: hubTagColorValueSchema,
});

export const updateHubAssigneeColorSchema = z.object({
	username: z.string().trim().min(1, "Choose someone.").max(80),
	color: hubTagColorValueSchema,
});

export const INVENTORY_COLUMN_PREF_KEYS = [
	"astId",
	"category",
	"description",
	"brand",
	"model",
	"serialNumber",
	"custody",
	"status",
	"purchaseDate",
	"purchasePrice",
	"ourWarrantyExpiry",
	"supplierWarrantyExpiry",
	"vendorId",
] as const;

export const updateHubInventoryColumnPrefsSchema = z.object({
	columns: z.record(z.string(), z.boolean()),
});

export const hubTaskCommentBodySchema = z.string().trim().min(1, "Write a comment.").max(2000);

export const createHubTaskCommentSchema = z.object({
	body: hubTaskCommentBodySchema,
});

export const updateHubTaskCommentSchema = z.object({
	body: hubTaskCommentBodySchema,
});

export const updateHubTaskSchema = z.object({
	title: hubText("task").optional(),
	description: z.string().trim().max(4000).optional(),
	assignee: z.string().trim().max(800).optional(),
	assignees: z.array(z.string().trim().max(80)).max(12).optional(),
	assigneeUserIds: z.array(uuid).max(12).optional(),
	dueOn: z.union([z.literal(""), z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, "Enter a date.")]).optional(),
	tag: z.string().trim().max(80).optional(),
	tags: z.array(z.string().trim().max(80)).max(12).optional(),
	status: hubTaskStatusSchema.optional(),
	checklist: hubTaskChecklistSchema.optional(),
	checklistTitle: z.string().trim().max(200).optional(),
	attachments: hubTaskAttachmentsSchema.optional(),
	boardPosition: z.number().finite().optional(),
});

export const createHubExpenseSchema = z.object({
	spentOn: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, "Enter a date."),
	description: hubText("expense"),
	category: z.enum(["fuel", "toll", "parking", "meals", "supplies", "tools", "transport", "utilities", "other"]),
	amount: z.string().trim().regex(/^\d+(\.\d{1,2})?$/, "Enter an amount."),
	payee: z.string().trim().max(200).optional().default(""),
});

export const HUB_BOOK_LISTS = [
	"deals",
	"items",
	"receipts",
	"leave",
	"attendance",
	"claims",
	"journals",
	"accounts",
	"moves",
	"organisations",
	"events",
	"inventory",
	"inventoryCategories",
	"inventoryBrands",
	"consumables",
	"consumableBalances",
	"consumableMovements",
	"consumableSettings",
	"customerSites",
	"projects",
	"workOrders",
	"internal",
	"assets",
	"phones",
	"phone-types",
	"register",
	"vehicles",
	"credentials",
	"maintenance",
	"repairs",
	"letterTemplates",
	"savedLetters",
	"auditYears",
	"auditFiles",
	"purchases.bills",
	"purchases.purchase-orders",
	"purchases.credits",
	"purchases.quotations",
	"purchases.sales-orders",
	"purchases.invoices",
] as const;

const hubBookListField = z.preprocess(
	(value) => (typeof value === "string" ? normalizeHubBookList(value) : value),
	z.enum(HUB_BOOK_LISTS),
);

export const appendHubBookSchema = z.object({
	list: hubBookListField,
	record: z.record(z.string(), z.unknown()),
}).superRefine((value, ctx) => {
	if (JSON.stringify(value.record).length > bookRecordLimit(value.list)) {
		ctx.addIssue({ code: "custom", message: "That record is too long." });
	}
});

export const reorderHubBookSchema = z.object({
	list: hubBookListField,
	ids: z.array(z.number().int().positive()).min(1).max(200),
});

export const deleteHubBookSchema = z.object({
	list: hubBookListField,
	id: z.union([z.number().int().positive(), z.string().trim().min(1).max(80)]),
});

export const updateHubBookSchema = z.object({
	list: hubBookListField,
	id: z.union([z.number().int().positive(), z.string().trim().min(1).max(80)]),
	record: z.record(z.string(), z.unknown()),
}).superRefine((value, ctx) => {
	if (JSON.stringify(value.record).length > bookRecordLimit(value.list)) {
		ctx.addIssue({ code: "custom", message: "That record is too long." });
	}
});

function bookRecordLimit(list: string): number {
	if (list === "auditFiles") return 8000000;
	if (list === "savedLetters") return 2000000;
	return list === "letterTemplates" ? 32000 : 8000;
}

export const createTeamChannelSchema = z.object({
	name: z.string().trim().min(1, "Enter a channel name.").max(80),
});

export const createTeamDirectSchema = z.object({
	userId: uuid,
});

export const inviteTeamMemberSchema = z.object({
	userId: uuid,
});

export const sendTeamMessageSchema = z.object({
	body: z.string().trim().min(1, "Write a message.").max(4000),
});

export const editTeamMessageSchema = sendTeamMessageSchema;

export const sendTeamMediaSchema = z.object({
	caption: z.string().trim().max(4000).optional(),
	albumId: z.string().uuid().optional(),
});

export const createSocialPostSchema = z.object({
	body: z.string().trim().min(1, "Write the post.").max(4000),
	channelIds: z.array(z.string().trim().min(1).max(80)).min(1, "Choose an account."),
	scheduledAt: z.string().datetime().nullable().optional(),
	mode: z.enum(["now", "schedule", "draft"]).optional(),
	images: z
		.array(
			z.object({
				id: z.string().trim().min(1).max(200),
				path: z
					.string()
					.trim()
					.min(1)
					.max(2000)
					.refine(
						(value) => value.startsWith("https://") || /^key-media:[0-9a-f-]{36}$/i.test(value),
						"Upload the photo again.",
					),
			}),
		)
		.max(10)
		.optional(),
});

export const confirmFacebookPagesSchema = z.object({
	selectionId: uuid,
	pageIds: z.array(z.string().trim().min(1).max(80)).min(1, "Choose a Page.").max(20),
});

export const connectSocialChannelSchema = z.object({
	network: z.enum(SOCIAL_NETWORKS),
	handle: z.string().trim().min(1, "Enter the account name.").max(80).optional(),
});

export const connectEngageAccountSchema = z.object({
	network: z.enum(ENGAGE_INBOX_NETWORKS),
	displayName: z.string().trim().min(1, "Enter a name.").max(80),
	providerAccountId: z.string().trim().min(1, "Enter the account id.").max(160),
	credential: z.string().trim().min(1).max(4000).optional(),
});

export const startEngageLoginSchema = z
	.object({
		network: z.enum(ENGAGE_INBOX_NETWORKS),
		nickname: z.string().trim().max(80).optional(),
		consentAcknowledged: z.boolean().optional(),
		method: z.enum(ENGAGE_LOGIN_METHODS).optional(),
		phone: z.string().trim().max(24).optional(),
		email: z.string().trim().max(254).optional(),
	})
	.superRefine((value, ctx) => {
		if (value.method === "email") {
			if (!value.email || !engageLooksLikeEmail(value.email)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["email"],
					message: "Enter the company LinkedIn email.",
				});
			}
			return;
		}
		if (value.method !== "phone_code") return;
		if (!value.phone) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["phone"],
				message: engageLoginPhoneHint(""),
			});
			return;
		}
		if (!normalizeEngageLoginPhone(value.phone)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["phone"],
				message: engageLoginPhoneHint(value.phone),
			});
		}
	})
	.transform((value) => ({
		...value,
		phone: value.phone ? (normalizeEngageLoginPhone(value.phone) ?? value.phone) : value.phone,
		email: value.email?.trim().toLowerCase(),
	}));

export const replyEngageSchema = z
	.object({
		body: z.string().trim().max(4000).optional().default(""),
		idempotencyKey: z.string().trim().min(8).max(80).optional(),
		image: z
			.object({
				mimeType: z.string().trim().min(3).max(80),
				bytesBase64: z.string().min(8).max(16_000_000),
				filename: z.string().trim().max(200).optional(),
			})
			.optional(),
		replyToMessageId: uuid.optional(),
		replyToName: z.string().trim().max(160).optional(),
		replyToBody: z.string().trim().max(4000).optional(),
	})
	.refine((value) => Boolean(value.body?.trim()) || Boolean(value.image), {
		message: "Write a reply or attach a file.",
	});

export const editEngageMessageSchema = z.object({
	body: z.string().trim().min(1, "Write the new message.").max(4000),
});

export const engageLoginChallengeClickSchema = z.object({
	x: z.number().min(0).max(4000),
	y: z.number().min(0).max(4000),
});

const engageLoginChallengeKeySchema = z.enum([
	"Enter",
	"Backspace",
	"Tab",
	"Escape",
	"ArrowUp",
	"ArrowDown",
	"ArrowLeft",
	"ArrowRight",
	"Delete",
	"Home",
	"End",
]);

export const engageLoginChallengeInteractSchema = z.discriminatedUnion("kind", [
	z.object({
		kind: z.literal("click"),
		x: z.number().min(0).max(4000),
		y: z.number().min(0).max(4000),
	}),
	z.object({
		kind: z.literal("type"),
		text: z.string().min(1).max(512),
	}),
	z.object({
		kind: z.literal("key"),
		key: engageLoginChallengeKeySchema,
	}),
	z.object({
		kind: z.literal("scroll"),
		deltaX: z.number().min(-8000).max(8000),
		deltaY: z.number().min(-8000).max(8000),
	}),
]);

export const engageLoginSecretSchema = z
	.object({
		password: z.string().min(1).max(8000).optional(),
		phone: z.string().trim().max(24).optional(),
		code: z.string().trim().min(3).max(8000).optional(),
	})
	.superRefine((value, ctx) => {
		if (!value.password && !value.phone && !value.code) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Enter the phone number, login code, password, or session cookie.",
			});
			return;
		}
		if (value.phone && !normalizeEngageLoginPhone(value.phone)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["phone"],
				message: engageLoginPhoneHint(value.phone),
			});
		}
	})
	.transform((value) => ({
		...value,
		phone: value.phone ? (normalizeEngageLoginPhone(value.phone) ?? value.phone) : value.phone,
	}));

export const inviteEngageMemberSchema = z.object({
	userId: uuid,
	role: z.enum(["admin", "agent"]),
});

export const engageNoteSchema = z.object({
	body: z.string().trim().min(1, "Write a note.").max(4000),
});

export const assignEngageSchema = z.object({
	assigneeId: uuid.nullable(),
});

export const pinEngageSchema = z.object({
	pinned: z.boolean(),
});

export const engageConnectorEventSchema = z.object({
	version: z.literal(1),
	connector_id: z.string().trim().min(1).max(80),
	event_id: z.string().trim().min(1).max(200),
	key_account_id: uuid,
	network: z.enum(ENGAGE_INBOX_NETWORKS),
	event_type: z.enum(ENGAGE_EVENT_TYPES),
	provider_conversation_id: z.string().trim().min(1).max(320),
	provider_message_id: z.string().trim().min(1).max(320).nullable(),
	occurred_at: z.string().datetime({ offset: true }),
	data: z
		.object({
			type: z.enum(["text", "media", "unsupported"]).optional(),
			body: z.string().max(20000).optional(),
			customer_name: z.string().trim().max(160).optional(),
			sender_name: z.string().trim().max(160).optional(),
			sender_handle: z.string().trim().max(320).optional(),
			name_trusted: z.boolean().optional(),
			customer_phone: z.string().trim().max(20).optional(),
			kind: z.enum(["dm", "group"]).optional(),
			pinned: z.boolean().optional(),
			participants: z
				.array(
					z.object({
						provider_id: z.string().trim().min(1).max(320),
						display_name: z.string().trim().max(160),
						phone: z.string().trim().max(20).optional(),
						username: z.string().trim().max(64).optional(),
					}),
				)
				.max(512)
				.optional(),
			direction: z.enum(["inbound", "outbound"]).optional(),
			status: z.string().trim().max(40).optional(),
			read_upto: z.number().int().positive().max(2_147_483_647).optional(),
			login_id: z.string().uuid().optional(),
			display_name: z.string().trim().max(160).optional(),
			provider_account_id: z.string().trim().max(160).optional(),
			media_sha256: z.string().trim().length(64).optional(),
			media_mime: z.string().trim().max(80).optional(),
			media_name: z.string().trim().max(200).optional(),
			media_base64: z.string().max(16_000_000).optional(),
			text_entities: z
				.array(
					z.object({
						offset: z.number().int().min(0).max(20_000),
						length: z.number().int().positive().max(20_000),
						type: z.enum(["italic", "bold", "underline", "strike", "code"]),
					}),
				)
				.max(64)
				.optional(),
			reply_to_provider_id: z.string().trim().max(320).optional(),
			reply_to_name: z.string().trim().max(160).optional(),
			reply_to_body: z.string().max(4000).optional(),
		})
		.default({}),
});

export const webPushSubscribeSchema = z.object({
	endpoint: z.string().url().max(2048),
	keys: z.object({
		p256dh: z.string().min(1).max(512),
		auth: z.string().min(1).max(512),
	}),
});

export const webPushUnsubscribeSchema = z.object({
	endpoint: z.string().url().max(2048),
});

export const webPushSettingsSchema = z.object({
	enabled: z.boolean(),
	soundEnabled: z.boolean(),
	mailboxes: z.array(
		z.object({
			mailboxId: uuid,
			enabled: z.boolean(),
		}),
	),
});
